// @flow
import { getAuthToken, getBidderId, getUserData } from './user';
import { getCurrentUrl, getCurrentUrlFromState } from './route';
import algoliaApi from '../api/algolia';
import api from '../api/buyNowRequest';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { EventTypes } from 'redux-segment';
import { getBiddingInfo } from './biddingInfo';
import { getCatalog } from './catalog';
import { getCatalogRegistration } from './catalogRegistration';
import { getCategoryLevel } from '../../utils/categoryGrabber';
import { getDeployment } from '../../redux/modules/config';
import { getItem } from './item';
import { getItemFacets } from './itemFacets';
import getPageNameFromPath from '../../utils/getPageNameFromPath';
import { getResellerType } from './reseller';
import { getSeller } from './seller';
import type { GlobalState } from '../rootReducer';
import { isTopRatedHouse } from './topRatedSellers';
import { isWhiteLabel } from './whiteLabel';
import { LOAD_CATEGORY_RESULTS_SUCCESS } from './landingPage';
import { LOAD_SEARCH_RESULTS_SUCCESS } from '../../utils/searchUtils';
import { modalTypes } from '../../enums';
import { parseQueryParams } from '../../utils/queryParams';
import { separateURL } from '../../utils/urls';

const KNOWN_SEARCH_ENGINES = [
    '.msn.',
    '.search.com',
    'about.com',
    'adsensecustomsearchads.com',
    'alice.',
    'alltheweb.com',
    'altavista.com',
    'ask.com',
    'avg.com',
    'babylon.com',
    'baidu.',
    'biglobe.',
    'bing.',
    'centrum.cz',
    'cnn.com/search',
    'daum.net',
    'delta-search.com',
    'discretesearch.com',
    'duckduckgo.com',
    'easydialsearch.com',
    'ekolay.net',
    'eniro.se',
    'haosou.com',
    'kvasir.no',
    'lycos.',
    'mamma.com',
    'mynet.com',
    'myprivatesearch.com',
    'mysearchdial.com',
    'najdi.',
    'naver.com',
    'onet.pl',
    'onetcenter.',
    'ozu.es',
    'pchome.com',
    'query.searchdconvertnow.com',
    'rakuten.',
    'rambler.ru',
    'search.aol',
    'search.avira.net',
    'search.bt.com',
    'search.centurylink.com',
    'search.colonymt.com',
    'search.earthlink.net',
    'search.excite.com',
    'search.frontier.com',
    'search.media.telstra.com',
    'search.mediacomcable.com',
    'search.monstercrawler.com',
    'search.myway.com',
    'search.pch.com',
    'search.smt.docomo',
    'search.socialdownloadr.com',
    'search.twcc.com',
    'search.us.com',
    'search.xfinity.com',
    'search.yahoo.com',
    'searchall.com',
    'searchassist.verizon.com',
    'searchguide.windstream.net',
    'searchlock.com',
    'searchprivacy.co',
    'searcht.sosodesktop.com',
    'searchthis.com',
    'securesearch.co',
    'sesam.no',
    'seznam.cz',
    'so.com',
    'sogou.com',
    'startsiden.no',
    'szukacz.pl',
    'terra.com',
    'thesmartsearch.net',
    'utorrent.inspsearch.com',
    'virgilio.it',
    'voila.fr',
    'w.goo.ne.jp',
    'w.google.',
    'w.yahoo.com',
    'wp.pl',
    'www.delta-search.com',
    'yam.com',
    'yandex.',
    'yoursearch.me',
    'zdsearch.com',
];
const KNOWN_SOCIAL_NETWORKS = [
    'pinterest.',
    'twitter.',
    '/t.co',
    'facebook.',
    'yuku.',
    'netvibes.',
    'blogspot.',
    'houzz.',
    'polyvore.',
    'youtube.',
    'vk.com',
    'paper.li',
    'reddit.',
    'buzzfeed.',
    'plus.google.',
    'plus.url.google',
    'ravelry',
    'wikia.',
    'wordpress.',
    'blog.goo.ne',
    'linkedin.',
    'groups.google.',
    '.ning.',
    'stackexchange.',
    'stumbleupon.',
    'tumblr.',
    'typepad.',
    'answers.yahoo.',
];
const IGNORED_REFERRERS = ['liveauctioneers.', 'optimizely.', 'atlassian.', 'localhost', 'rainforestqa.'];

/* ACTION TYPES */
export const IDENTIFY_ANALYTICS = 'IDENTIFY_ANALYTICS';
export const SAVE_FULLSTORY_SESSION_URL = 'SAVE_FULLSTORY_SESSION_URL';
export const TRACK_ATTRIBUTION_DATA = 'TRACK_ATTRIBUTION_DATA';
export const TRACK_CLICK = 'TRACK_CLICK';
export const TRACK_CLICK_FOR_ALGOLIA = 'TRACK_CLICK_FOR_ALGOLIA';
export const TRACK_CONVERSION_FOR_ALGOLIA = 'TRACK_CONVERSION_FOR_ALGOLIA';
export const TRACK_PAGEVIEW = 'TRACK_PAGEVIEW';
export const TRACK_SOCIAL_NETWORK_SHARE = 'TRACK_SOCIAL_NETWORK_SHARE';

/* ANALYTIC TYPES */

// THIS OBJECT SHOULD BE COMMON ACROSS THE APPS.  IF YOU UPDATE IT HERE, UPDATE IT EVERYWHERE
export const AnalyticsTypes = {
    Page: {
        AboutUs: 'about us',
        AccountSettings: 'account settings',
        AddCreditCard: 'add credit card',
        AuctionCalendar: 'catalog search',
        AuctionResults: 'auction results',
        BiddingConsole: 'bidding console',
        CardOnFile: 'card on file',
        Catalog: 'catalog',
        CatalogTerms: 'terms and conditions',
        Category: 'category',
        CertCapture: 'cert capture',
        Checkout: 'checkout page',
        CheckoutConfirmation: 'checkout confirmation',
        Consignments: 'consignments',
        CookiePolicy: 'cookie policy',
        CuratedCollection: 'curated collection',
        Discover: 'discover',
        Features: 'features',
        FollowedSellers: 'followed sellers',
        GetHelp: 'get help',
        Home: 'home',
        HowAuctionsWork: 'how it works',
        IntellectualProperty: 'intellectual property',
        Item: 'item',
        ItemPhotoViewer: 'item photo viewer',
        Message: 'message',
        Messages: 'messages',
        Mobile: 'mobile',
        MyAuctions: 'my auctions',
        MyBidsPast: 'my bids - past tab',
        MyBidsUpcoming: 'my bids - upcoming tab',
        MyProfile: 'my profile',
        NearMe: 'near me',
        Notifications: 'notifications',
        PasswordReset: 'password reset',
        PriceGuide: 'price guide',
        Privacy: 'privacy',
        PRServices: 'pr services',
        PushNotifications: 'push notifications',
        RecentItems: 'recent items',
        RecommendedItems: 'recommended items',
        Review: 'review',
        SavedItems: 'saved items',
        SavedSearchManage: 'saved searches - manage tab',
        SavedSearchRecent: 'saved searches - recent tab',
        Search: 'search dropdown',
        SearchItems: 'search',
        Seller: 'auctioneer',
        SellerDirectory: 'directory',
        SellerReviews: 'reviews',
        SellerTestimonials: 'testimonials',
        SoldItem: 'sold item',
        Storefront: 'storefront',
        Terminology: 'terminology',
        TermsAndConditions: 'terms and conditions',
        TrendingAuctions: 'trending auctions',
        WonItems: 'won items',
    },
    Track: {
        CompleteProfile: 'Completed Profile',
        FollowSeller: 'Followed Auctioneer',
        PlaceAbsenteeBid: 'Placed Absentee Bid',
        PlaceLiveBid: 'Placed Live Bid',
        RegisterForAuction: 'Requested Auction Registration',
        RegisterUser: 'Registered Account',
        RetractAbsenteeBid: 'Retracted Absentee Bid',
        SaveItem: 'Saved Item',
        SaveSearch: 'Followed Search',
        SetPassword: 'Set Password',
        ShareItem: 'Social Network Share',
        UnfollowSeller: 'Unfollowed Auctioneer',
        UnsaveItem: 'Unsaved Item',
        UnsaveSearch: 'Unfollowed Search',
    },
};

// reducer
const DEFAULT_STATE = {
    algoliaAnalytics: {
        queryId: '',
        usedIndex: '',
    },
    anonymousId: '',
    attributionData: {
        bidderId: 0,
        campaign: 'Placeholder campaign Data',
        content: 'Placeholder content Data',
        keyword: 'Placeholder keyword Data',
        medium: 'Placeholder medium Data',
        source: 'Placeholder source Data',
        touchDate: 0,
    },
    fullStorySessionUrl: '',
    referrer: '',
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_SEARCH_RESULTS_SUCCESS:
        case LOAD_CATEGORY_RESULTS_SUCCESS:
            return {
                ...state,
                algoliaAnalytics: {
                    queryId: action.payload.queryId,
                    usedIndex: action.payload.usedIndex,
                },
            };
        case SAVE_FULLSTORY_SESSION_URL:
            return {
                ...state,
                fullStorySessionId: action.payload.sessionID || '',
                fullStorySessionUrl: action.payload.sessionUrl || '',
            };
        case TRACK_ATTRIBUTION_DATA:
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.analytics;

const getReferrer = createSelector(stateSelector, state => state.referrer);
export const getFullStorySessionUrl = createSelector(stateSelector, state => state.fullStorySessionUrl);

export const getFullStorySessionId = createSelector(stateSelector, state => state.fullStorySessionId || 'N/A');

const getSailthru = () => {
    return typeof window !== 'undefined' ? window.Sailthru : () => false;
};

const getTouchDate = createSelector(stateSelector, state => state.touchDate);

const getAlgoliaAnalytics = createSelector(stateSelector, state => state.algoliaAnalytics);

const searchList = (haystack, needle) => {
    let ret = -1;
    haystack.forEach(value => {
        if (needle.toLowerCase().includes(value)) {
            ret = needle;
        }
    });
    return ret;
};

export const getAttributionData = (state: any) => {
    const { search } = getCurrentUrl(state);
    const rawReferrer = getReferrer(state);
    const referrer = separateURL(rawReferrer);
    const bidderId = getBidderId(state);
    const touchDate = getTouchDate(state);

    let attributionData = {
        bidderId,
        campaign: '',
        content: '',
        keyword: '',
        medium: '',
        source: '',
        touchDate,
    };

    const engineName = searchList(KNOWN_SEARCH_ENGINES, rawReferrer);
    const networkName = searchList(KNOWN_SOCIAL_NETWORKS, rawReferrer);
    const referrerName = searchList(IGNORED_REFERRERS, rawReferrer);
    const queryParams = parseQueryParams(search);
    if (queryParams.gclid) {
        attributionData = {
            ...attributionData,
            campaign: 'adwords',
            content: 'adwords',
            keyword: queryParams.gclid,
            medium: 'cpc',
            source: 'google',
        };
    } else if (queryParams.gclsrc) {
        attributionData = {
            ...attributionData,
            campaign: 'DoubleClick',
            content: 'DoubleClick',
            keyword: queryParams.gclsrc || '',
            medium: 'cpc',
            source: 'google',
        };
    } else if (queryParams.campaignsource || queryParams.campaignSource) {
        attributionData = {
            ...attributionData,
            campaign: queryParams.campaignname || '',
            content: queryParams.campaigncontent || '',
            keyword: queryParams.campaignkeyword || '',
            medium: queryParams.campaignmedium || '',
            source: queryParams.campaignsource || queryParams.campaignSource || '',
        };
    } else if (queryParams.utm_source) {
        attributionData = {
            ...attributionData,
            campaign: queryParams.utm_campaign || '',
            content: queryParams.utm_content || '',
            keyword: queryParams.utm_term || '',
            medium: queryParams.utm_medium || '',
            source: queryParams.utm_source || '',
        };
    } else if (engineName !== -1) {
        attributionData = {
            ...attributionData,
            content: referrer.path,
            medium: 'organic',
            source: referrer.host,
        };
    } else if (networkName !== -1) {
        attributionData = {
            ...attributionData,
            content: referrer.path,
            medium: 'social',
            source: referrer.host,
        };
    } else if (referrer.host !== '' && referrerName === -1) {
        attributionData = {
            ...attributionData,
            content: referrer.path,
            medium: 'referral',
            source: referrer.host,
        };
    } else {
        attributionData = {
            ...attributionData,
            campaign: 'direct',
            source: 'direct',
        };
    }
    return attributionData;
};

const getItemPrice = (salePrice, leadingBid) => salePrice || leadingBid;

const mapCategoryNames = facets => {
    const newFacets = cloneDeep(facets);
    for (const prop in facets) {
        if (Object.prototype.hasOwnProperty.call(newFacets, prop)) {
            newFacets[prop] = newFacets[prop].map(facet => {
                const facetLevel = getCategoryLevel(facet);
                if (facetLevel.categoryName) {
                    return facetLevel.categoryName;
                }
                return facet;
            });
        }
    }
    return newFacets;
};

const getItemAnalytics = (state: any, itemId, event) => {
    const item = getItem(state, itemId);
    const biddingInfo = getBiddingInfo(state, itemId);
    const itemFacets = getItemFacets(state, itemId);
    const facets = mapCategoryNames(itemFacets);
    const catalog = getCatalog(state, item.catalogId);
    const { leadingBid } = getBiddingInfo(state, itemId);
    const { salePrice, sellerId, title } = item;
    const topRatedHouse = isTopRatedHouse(state, sellerId);

    const { buyersPremium, catalogId, catalogStatus } = catalog;

    const {
        categories = 'n/a',
        creators = 'n/a',
        materialsTechniques = 'n/a',
        origins = 'n/a',
        stylePeriods = 'n/a',
    } = facets;

    const price = getItemPrice(salePrice, leadingBid);

    const formatAnalytics = {
        analytics: {
            eventPayload: {
                event,
                properties: {
                    biddingInfo,
                    buyersPremium,
                    catalogId,
                    catalogStatus,
                    categories,
                    creators,
                    houseId: sellerId,
                    itemId,
                    materialsTechniques,
                    origins,
                    price,
                    stylePeriods,
                    title,
                    topRatedHouse,
                },
            },
            eventType: EventTypes.track,
        },
    };
    return formatAnalytics;
};

export const getSaveItemAnalytics = (state: any, itemId: number) =>
    getItemAnalytics(state, itemId, AnalyticsTypes.Track.SaveItem);

export const getUnsaveItemAnalytics = (state: any, itemId: number) =>
    getItemAnalytics(state, itemId, AnalyticsTypes.Track.UnsaveItem);

const getSellerAnalytics = (state: any, sellerId, event) => {
    const topRatedHouse = isTopRatedHouse(state, sellerId);

    const formatAnaltyics = {
        analytics: {
            eventPayload: {
                event,
                properties: {
                    houseId: sellerId,
                    topRatedHouse,
                },
            },
            eventType: EventTypes.track,
        },
    };
    return formatAnaltyics;
};
export const getFollowSearchAnalytics = () => ({
    analytics: {
        eventPayload: {
            event: AnalyticsTypes.Track.SaveSearch,
        },
        eventType: EventTypes.track,
    },
});
export const getUnfollowSearchAnalytics = () => ({
    analytics: {
        eventPayload: {
            event: AnalyticsTypes.Track.UnsaveSearch,
        },
        eventType: EventTypes.track,
    },
});
export const getFollowSellerAnalytics = (state: any, sellerId: number) =>
    getSellerAnalytics(state, sellerId, AnalyticsTypes.Track.FollowSeller);

export const getUnfollowSellerAnalytics = (state: any, sellerId: number) =>
    getSellerAnalytics(state, sellerId, AnalyticsTypes.Track.UnfollowSeller);

export const getCompleteAccountAnalytics = () => ({
    analytics: {
        eventPayload: {
            event: AnalyticsTypes.Track.CompleteProfile,
        },
        eventType: EventTypes.track,
    },
});

const registerAnalytics = (state: any, bidderId, attributionData, event) => {
    const whiteLabel = isWhiteLabel(state);
    return {
        eventPayload: {
            event,
            properties: {
                attribution_bidderId: bidderId,
                attribution_campaign: attributionData.campaign,
                attribution_content: attributionData.content,
                attribution_keyword: attributionData.keyword,
                attribution_medium: attributionData.medium,
                attribution_source: attributionData.source,
                attribution_touchdate: attributionData.touchDate,
                whiteLabel,
            },
        },
        eventType: EventTypes.track,
    };
};

export const getRegisterAccountAnalytics = (state: any, bidderId: number, attributionData: any) =>
    registerAnalytics(state, bidderId, attributionData, AnalyticsTypes.Track.RegisterUser);

export const getSetPasswordAnalytics = (state: any, bidderId: number, attributionData: any) =>
    registerAnalytics(state, bidderId, attributionData, AnalyticsTypes.Track.SetPassword);

export const getLoginAnalytics = (userData: any, resellerType: number) => ({
    analytics: {
        eventPayload: {
            traits: {
                annualNms: userData.ANMS,
                createdAt: userData.createdDate,
                creditCardValidated: userData.creditCardValidated,
                email: userData.email,
                firstName: userData.firstName,
                lastName: userData.lastName,
                loggedIn: userData.bidderId > 0,
                profileComplete: userData.profileComplete,
                resellerType: resellerType,
            },
            userId: userData.bidderId,
        },
        eventType: EventTypes.identify,
    },
});

export const getRequestedAuctionRegistrationAnalytics = (state: any, catalogId: number, itemId: number = 0) => {
    // we want to include itemId if we know if so we can track click per item
    const whiteLabel = isWhiteLabel(state);
    const { catalogStatus, isCatalogOnly, sellerId } = getCatalog(state, catalogId) || {};
    const topRatedHouse = isTopRatedHouse(state, sellerId);
    const auctionAnalytics = {
        analytics: {
            eventPayload: {
                event: AnalyticsTypes.Track.RegisterForAuction,
                properties: {
                    catalogId,
                    catalogStatus,
                    isCatalogOnly,
                    itemId,
                    sellerId,
                    topRatedHouse,
                    whiteLabel,
                },
            },
            eventType: EventTypes.track,
        },
    };
    return auctionAnalytics;
};

const getPlacedBidAnalytics = (state: any, itemId, bidAmount, event, biddingStatus) => {
    // TODO: we need to decide what the proper definition of 'bidCount' is and then implement
    const { catalogId, salePrice, sellerId, title } = getItem(state, itemId);
    const { buyersPremium, catalogStatus, currency } = getCatalog(state, catalogId);
    const { leadingBid } = getBiddingInfo(state, itemId);
    const catalogRegistration = getCatalogRegistration(state, catalogId).approved;
    const userData = getUserData(state);
    const bidderId = userData.bidderId;
    const price = getItemPrice(salePrice, leadingBid);
    const whiteLabel = isWhiteLabel(state);
    const topRatedHouse = isTopRatedHouse(state, sellerId);
    const locationPath = getCurrentUrlFromState(state);
    const pageName = getPageNameFromPath(locationPath);
    // const bidCount = 1;
    const formatAnalytics = {
        eventPayload: {
            event,
            properties: {
                approvedForAuction: catalogRegistration,
                // bidCount,
                bidderId,
                biddingStatus,
                buyersPremium,
                catalogId,
                catalogStatus,
                currency,
                houseId: sellerId,
                itemId,
                orderId: `${itemId}-${bidderId}`,
                pageName,
                products: [
                    {
                        id: itemId,
                        name: title,
                        price,
                        quantity: 1,
                    },
                ],
                topRatedHouse,
                total: bidAmount,
                value: bidAmount,
                whiteLabel,
            },
        },
        eventType: EventTypes.track,
    };

    return formatAnalytics;
};

export const getPlacedAbsenteeBidAnalytics = (state: any, itemId: number, bidAmount: number) =>
    getPlacedBidAnalytics(state, itemId, bidAmount, AnalyticsTypes.Track.PlaceAbsenteeBid, 'absentee');

export const getPlacedLiveBidAnalytics = (state: any, itemId: number, bidAmount: number) =>
    getPlacedBidAnalytics(state, itemId, bidAmount, AnalyticsTypes.Track.PlaceLiveBid, 'live');

export const getRetractedAbsenteeBidAnalytics = (
    state: any,
    itemId: number,
    retractionReason: any,
    retractionExplanation: any
) => {
    const userData = getUserData(state);
    const bidderId = userData.bidderId;

    const formatAnalytics = {
        analytics: {
            eventPayload: {
                event: AnalyticsTypes.Track.RetractAbsenteeBid,
                properties: {
                    bidderId,
                    itemId,
                    retractionExplanation,
                    retractionReason,
                },
            },
            eventType: EventTypes.track,
        },
    };

    return formatAnalytics;
};

/* ACTION CREATORS */
const sailthruTrack = url => {
    const sailthru = getSailthru();
    if (sailthru) {
        const path = separateURL(url).path ? separateURL(url).path : url;
        const finalURL = `https://www.liveauctioneers.com${path}`;
        try {
            sailthru.track('pageview', {
                url: finalURL,
            });
        } catch (e) {
            // commenting out this error because its pointless
            // console.log('Edge thinks this method wants an object, but it\'s wrong!');
        }
    }
};

export const saveFullStorySessionData = (sessionData: any) => ({
    payload: sessionData,
    type: SAVE_FULLSTORY_SESSION_URL,
});

const trackPageView = (pageType: string, props: any = {}) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const currentUrl = getCurrentUrlFromState(state);

    sailthruTrack(currentUrl);
    return dispatch({
        meta: {
            actionTime: Date.now(),
            analytics: {
                eventPayload: {
                    properties: {
                        pageType,
                        url: currentUrl,
                        ...props,
                    },
                },
                eventType: EventTypes.page,
            },
        },
        payload: {},
        type: TRACK_PAGEVIEW,
    });
};

export const recordAnalyticsForAboutUsPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.AboutUs));

export const recordAnalyticsForAccountSettingsPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.AccountSettings));

export const recordAnalyticsForAddCreditCardPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.AddCreditCard));

export const recordAnalyticsForAuctionResultsPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.AuctionResults));

export const recordAnalyticsForBidConsolePageView = (catalogId: number) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const catalog = getCatalog(state, catalogId);
    const { catalogStatus, saleStartTs, sellerId, title } = catalog;

    return dispatch(
        trackPageView(AnalyticsTypes.Page.BiddingConsole, {
            catalogId,
            catalogStatus,
            houseId: sellerId,
            saleStartTs,
            title,
        })
    );
};

export const recordAnalyticsForCatalogPageView = (catalogId: number) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const catalog = getCatalog(state, catalogId);
    const { catalogStatus, saleStartTs, sellerId, title } = catalog;
    const topRatedHouse = isTopRatedHouse(state, sellerId);

    return dispatch(
        trackPageView(AnalyticsTypes.Page.Catalog, {
            catalogId,
            catalogStatus,
            houseId: sellerId,
            saleStartTs,
            title,
            topRatedHouse,
        })
    );
};

export const recordAnalyticsForAuctionCalendarPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.AuctionCalendar));

export const recordAnalyticsForCategoryLandingPageView = (pageType: string) => async (
    dispatch: Function,
    getState: Function
) => dispatch(trackPageView(pageType));

export const recordAnalyticsForCheckoutConfirmationPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.CheckoutConfirmation));

export const recordAnalyticsForCheckoutPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.Checkout));

export const recordAnalyticsForConsignmentsPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.Consignments));

export const recordAnalyticsForCookiePolicyPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.CookiePolicy));

export const recordAnalyticsForCuratedCollectionPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.CuratedCollection));

export const recordAnalyticsForFeaturesPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.Features));

export const recordAnalyticsForFlattenTheCurvePageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView('flatten the curve'));

export const recordAnalyticsForFollowedSellersPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.FollowedSellers));

export const recordAnalyticsForHomePageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.Home));

export const recordAnalyticsForHowAuctionsWorkPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.HowAuctionsWork));

export const recordAnalyticsForIdentify = (bidder: any) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const resellerType = getResellerType(state);
    dispatch({
        meta: getLoginAnalytics(bidder, resellerType),
        payload: {},
        type: IDENTIFY_ANALYTICS,
    });
};

export const recordAnalyticsForIntellectualPropertyPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.IntellectualProperty));

export const recordAnalyticsForItemPageView = (itemId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const item = getItem(state, itemId);
    const catalog = getCatalog(state, item.catalogId);
    const biddingInfo = getBiddingInfo(state, itemId);
    const facets = getItemFacets(state, itemId);

    const { salePrice, sellerId, title } = item;
    const { buyersPremium, catalogId, catalogStatus } = catalog;
    const { categories, creators, materialsTechniques, origins, stylePeriods } = facets;
    const { leadingBid } = biddingInfo;

    const pageType = catalogStatus === 'done' ? AnalyticsTypes.Page.SoldItem : AnalyticsTypes.Page.Item;

    const topRatedHouse = isTopRatedHouse(state, sellerId);

    return dispatch(
        trackPageView(pageType, {
            buyersPremium,
            catalogId,
            catalogStatus,
            categories,
            creators,
            houseId: sellerId,
            itemId,
            materialsTechniques,
            origins,
            salePrice: salePrice || leadingBid,
            stylePeriods,
            title,
            topRatedHouse,
        })
    );
};

export const recordAnalyticsForMessagePageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.Message));

export const recordAnalyticsForMessagesPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.Messages));

export const recordAnalyticsForMobilePageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.Mobile));

export const recordAnalyticsForMyAuctionsPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.MyAuctions));

const recordAnalyticsForMyBidsUpcomingPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView('my bids - upcoming tab'));

const recordAnalyticsForMyBidsPastPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView('my bids - past tab'));

export const recordAnalyticsForMyBidsPageView = (tab: boolean | string) => async (
    dispatch: Function,
    getState: Function
) => {
    if (tab === 'upcoming') {
        return dispatch(recordAnalyticsForMyBidsUpcomingPageView());
    } else if (tab === 'ended') {
        return dispatch(recordAnalyticsForMyBidsPastPageView());
    }
};

export const recordAnalyticsForNearMePageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.NearMe));

export const recordAnalyticsForPasswordResetPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.PasswordReset));

export const recordAnalyticsForPrivacyPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.Privacy));

export const recordAnalyticsForPRServicesPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.PRServices));

export const recordAnalyticsForRecentItemsPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.RecentItems));

export const recordAnalyticsForRecommendedItemsPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.RecommendedItems));

export const recordAnalyticsForReviewPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.Review));

export const recordAnalyticsForSavedItemsPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.SavedItems));

export const recordAnalyticsForSavedSearchesPageView = (pageType: string) => async (
    dispatch: Function,
    getState: Function
) => dispatch(trackPageView(pageType));

export const recordAnalyticsForSellerDirectoryPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.SellerDirectory));

export const recordAnalyticsForSellerTestimonialsPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.SellerTestimonials));

export const recordAnalyticsForSellerPageView = (sellerId: number) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const seller = getSeller(state, sellerId);
    const { name } = seller;
    const topRatedHouse = isTopRatedHouse(state, sellerId);

    return dispatch(
        trackPageView(AnalyticsTypes.Page.Seller, {
            houseId: sellerId,
            name,
            topRatedHouse,
        })
    );
};

export const recordAnalyticsForStorefrontPageView = (houseId: number) => async (
    dispatch: Function,
    getState: Function
) => dispatch(trackPageView(AnalyticsTypes.Page.Storefront, { houseId }));

export const recordAnalyticsForSellerReviewsPageView = (houseId: number) => async (
    dispatch: Function,
    getState: Function
) => dispatch(trackPageView(AnalyticsTypes.Page.SellerReviews, { houseId }));

export const recordAnalyticsForSearchItemsPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.SearchItems));

export const recordAnalyticsForTermsAndConditionsPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.TermsAndConditions));

export const recordAnalyticsForCatalogTermsPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.CatalogTerms));

export const recordAnalyticsForTerminologyPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.Terminology));

export const recordAnalyticsForTrendingPageView = () => async (dispatch: Function, getState: Function) =>
    dispatch(trackPageView(AnalyticsTypes.Page.TrendingAuctions));

export const recordAnalyticsForWonItemsPageView = () => async (dispatch: Function, getState: Function) => {
    dispatch(trackPageView(AnalyticsTypes.Page.WonItems));
};
export const recordAnalyticsForCertCapturePageView = (bidderId: number, state: string) => async (
    dispatch: Function,
    getState: Function
) => {
    dispatch(trackPageView(AnalyticsTypes.Page.CertCapture, { bidderId, state }));
};

const getGAEventAnalytics = (action, category, label) => {
    return {
        analytics: {
            eventPayload: {
                event: action,
                properties: {
                    action,
                    category,
                    label,
                },
            },
            eventType: EventTypes.track,
        },
    };
};

const getGAEventAnalyticsForBuyNowOffer = properties => {
    return {
        analytics: {
            eventPayload: {
                event: properties.action,
                properties,
            },
            eventType: EventTypes.track,
        },
    };
};

export const trackClick = (action: string, label: string) => async (dispatch: Function, getState: Function) => {
    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackAdUnitClick = ({
    adUnitId,
    catalogId,
    sellerId,
}: {
    adUnitId: number,
    catalogId: number,
    sellerId: number,
}) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const bidderId = getBidderId(state);

    const action = `Clicked ${catalogId ? 'Catalog' : 'Consignmet'} Ad Unit`;
    let label = `sideBanner-${adUnitId}-${catalogId || sellerId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackACNClick = (label: string) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const bidderId = getBidderId(state);

    const action = 'Clicked Homepage ACN Module';
    let labelToUse = `${label}`;
    if (bidderId !== -1) {
        labelToUse += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', labelToUse);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackHeroClick = (label: string) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const bidderId = getBidderId(state);

    const action = 'Clicked Homepage Hero Banner Module';
    let labelToUse = `${label}`;
    if (bidderId !== -1) {
        labelToUse += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', labelToUse);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackFeaturedCatalogClick = (catalogId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const { sellerId } = getCatalog(state, catalogId);
    const bidderId = getBidderId(state);

    const action = 'Clicked Homepage Featured Auctions Module';
    let label = `featured-${catalogId}-${sellerId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackPersonalizeBannerClick = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const bidderId = getBidderId(state);

    const action = 'Clicked Homepage Personalize Banner';
    let label = `personalize-banner-bidder-${bidderId}`;

    const analytics = getGAEventAnalytics(action, 'Personalize', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackLiveAuctionBarClick = (catalogId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const { sellerId } = getCatalog(state, catalogId);
    const bidderId = getBidderId(state);

    const action = 'Clicked Homepage Live Auction Module';
    let label = `live-auction-bar-${catalogId}-${sellerId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackFeaturedCategoryClick = (label: string) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const bidderId = getBidderId(state);

    const action = 'Clicked Homepage Featured Categories Module';
    let labelToUse = `${label}`;
    if (bidderId !== -1) {
        labelToUse += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', labelToUse);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackRecentItemClick = (itemId: number, position: any) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const { catalogId } = getItem(state, itemId);
    const { sellerId } = getCatalog(state, catalogId);
    const bidderId = getBidderId(state);

    const action = 'Clicked Homepage Recent Items Module';
    let label = `Recent-Items-${itemId}-${sellerId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackRecommendedItemClick = (itemId: number, position: any) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const { catalogId } = getItem(state, itemId);
    const { sellerId } = getCatalog(state, catalogId);
    const bidderId = getBidderId(state);

    const action = 'Clicked Homepage Recommended Items Module';
    let label = `Recommended-Items-${itemId}-${sellerId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackFromFollowedSearchClick = (itemId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const bidderId = getBidderId(state);

    const action = 'Clicked Homepage Followed Search Module';
    let label = `followed-search-${itemId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackFromSearchFollowSuggestionClick = ({
    categoryId,
    keyword,
}: {
    categoryId: any,
    keyword: string,
}) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const bidderId = getBidderId(state);

    const action = 'Clicked Homepage Search Follow Suggestions Follow Button';
    let label = 'search-follow-suggestion';
    if (categoryId > 0) {
        label += `-${categoryId}`;
    }
    if (keyword !== undefined && keyword.length > 0) {
        label += `-${keyword}`;
    }
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackPastAuctionResultsClick = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const bidderId = getBidderId(state);

    const action = 'Clicked Past Auction Results';
    let label = 'Past-Auction-Results';
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackPriceResultsClick = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const bidderId = getBidderId(state);

    const action = 'Clicked Price Results';
    let label = 'Price-Results';
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackTrendingItemClick = (itemId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const { catalogId } = getItem(state, itemId);
    const { sellerId } = getCatalog(state, catalogId);
    const bidderId = getBidderId(state);

    const action = 'Clicked Homepage Trending Items Module';
    let label = `Trending-Items-${itemId}-${sellerId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackUpcomingCatalogClick = (catalogId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const { catalogStatus, sellerId } = getCatalog(state, catalogId);
    const bidderId = getBidderId(state);
    const status = catalogStatus === 'online' ? 'upcoming' : catalogStatus;

    const action = 'Clicked Homepage Live and Upcoming Auctions Module';
    let label = `${status}-${catalogId}-${sellerId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackAuctionsYouMayLikeClick = (catalogId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const { catalogStatus, sellerId } = getCatalog(state, catalogId);
    const bidderId = getBidderId(state);
    const status = catalogStatus === 'online' ? 'upcoming' : catalogStatus;

    const action = 'Clicked Homepage Auctions You May Like Module';
    let label = `${status}-${catalogId}-${sellerId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackCatalogsNearCarouselClick = (catalogId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const { catalogStatus, sellerId } = getCatalog(state, catalogId);
    const bidderId = getBidderId(state);
    const status = catalogStatus === 'online' ? 'upcoming' : catalogStatus;

    const action = 'Clicked Homepage Auctions Near Module';
    let label = `${status}-${catalogId}-${sellerId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackTodayClick = (catalogId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const { catalogStatus, sellerId } = getCatalog(state, catalogId);
    const bidderId = getBidderId(state);
    const status = catalogStatus === 'online' ? 'upcoming' : catalogStatus;

    const action = 'Clicked catalog card on the Today page';
    let label = `${status}-${catalogId}-${sellerId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackAddToCalendarClick = (catalogId: number, calendarType: any) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const { sellerId } = getCatalog(state, catalogId);
    const bidderId = getBidderId(state);

    const action = 'Clicked Add to Calendar';
    let label = `${calendarType}-${catalogId}-${sellerId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const recordAttributionAnalytics = (data: any) => ({
    meta: {
        analytics: {
            eventPayload: {
                properties: {
                    attribution_bidderId: data.bidderId,
                    attribution_campaign: data.campaign,
                    attribution_content: data.content,
                    attribution_keyword: data.keyword,
                    attribution_medium: data.medium,
                    attribution_source: data.source,
                    attribution_touchdate: data.touchDate,
                    nonInteraction: 1,
                    should_store_in_db: true,
                },
            },
            eventType: EventTypes.track,
        },
    },
    payload: {},
    type: TRACK_ATTRIBUTION_DATA,
});

export const recordSocialNetworkShare = (network: string, itemUrl: string) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const bidderId = getBidderId(state);
    dispatch({
        meta: {
            analytics: {
                eventPayload: {
                    event: AnalyticsTypes.Track.ShareItem,
                    properties: {
                        bidderId,
                        itemUrl,
                        network,
                    },
                },
                eventType: EventTypes.track,
            },
        },
        payload: {},
        type: TRACK_SOCIAL_NETWORK_SHARE,
    });
};

export const trackComingUpItemsClick = (itemId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const { catalogId } = getItem(state, itemId);
    const { sellerId } = getCatalog(state, catalogId);
    const bidderId = getBidderId(state);

    const action = 'Clicked Homepage Coming Up Items Module';
    let label = `Coming-Up-${itemId}-${sellerId}`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackZeroSearchResultsShelfClick = (shelfId: string) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const bidderId = getBidderId(state);

    const action = 'Clicked Zero Results Suggestion Shelf';
    let label = `ZeroResults ${shelfId} shelf`;
    if (bidderId !== -1) {
        label += `-${bidderId}`;
    }

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackDesktopNotificationsView = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const bidderId = getBidderId(state);

    const action = 'Viewed notifications on desktop';
    const label = `Viewed-Notifications-${bidderId}`;

    const analytics = getGAEventAnalytics(action, 'Navigation', label);
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};

export const trackConversionForAlgolia = (itemId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const deployment = getDeployment(state);
    const bidderId = getBidderId(state);
    const algoliaAnalytics = getAlgoliaAnalytics(state);

    if (!algoliaAnalytics || !algoliaAnalytics.queryId) {
        return;
    }

    await algoliaApi.trackConversion({ algoliaAnalytics, bidderId, deployment, itemId });
    return dispatch({
        payload: {
            itemId,
        },
        type: TRACK_CONVERSION_FOR_ALGOLIA,
    });
};

export const trackClickForAlgolia = (itemId: number, position: number) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const deployment = getDeployment(state);
    const bidderId = getBidderId(state);
    const algoliaAnalytics = getAlgoliaAnalytics(state);

    if (!algoliaAnalytics || !algoliaAnalytics.queryId) {
        return;
    }

    await algoliaApi.trackClick({ algoliaAnalytics, bidderId, deployment, itemId, position });

    return dispatch({
        payload: {
            itemId,
            position,
        },
        type: TRACK_CLICK_FOR_ALGOLIA,
    });
};

export const trackBuyNowOfferSubmissionClick = (
    itemId: number,
    buyNowPrice: number,
    offerPrice: number,
    currency: string
) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);
    const bidderId = getBidderId(state);
    const { catalogId } = getItem(state, itemId);
    const { sellerId } = getCatalog(state, catalogId);
    const topRatedHouse = isTopRatedHouse(state, sellerId);

    let action;

    switch (state.makeOffer.modalType) {
        case modalTypes.MAKE_OFFER:
            action = 'Made An Offer';
            break;
        case modalTypes.BUY_NOW:
            action = 'Requested To Buy Now';
            break;
        default:
            break;
    }

    const analytics = getGAEventAnalyticsForBuyNowOffer({
        action,
        bidderId,
        catalogId,
        currency,
        houseId: sellerId,
        itemId,
        offerPrice,
        topRatedHouse,
        value: offerPrice,
    });

    const details = {
        buyNowPrice,
        buyNowRequestType: action,
        houseId: sellerId,
        itemId,
        offerPrice,
    };

    api.postCaptureBuyNowRequest({ authToken, deployment, details });
    return dispatch({
        meta: { ...analytics },
        payload: {},
        type: TRACK_CLICK,
    });
};
